import { useEffect, useRef } from "react";
import { SortOrder } from "primereact/api";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { UserRole } from "../../app/userSlice";
import {
	setSearchFilter,
	setDistrictFilter,
	setSchoolFilter,
	setRoleFilter,
	setIncludeInactive,
	setSortField,
	setSortOrder,
	setPage,
	resetUsersListFilters,
} from "../../app/usersListFilterSlice";

// Persists the UsersList page's filter/sort/page state in Redux (backed by
// sessionStorage via redux-persist), so the values are restored when the user
// navigates away and back during the same login session. The persisted key
// ("usersListFilter") is distinct from other pages so each page's filters are
// stored independently.
//
// Since the filter UI itself differs by role (e.g. School Admin vs. District/
// State Admin), the filters are reset whenever the current user's role changes
// during the session, to avoid showing stale/irrelevant filter values.
export function useUsersListFilters() {
	const dispatch = useAppDispatch();
	const { role } = useAppSelector((state) => state.user);

	const searchFilter = useAppSelector((state) => state.usersListFilter.searchFilter);
	const districtFilter = useAppSelector((state) => state.usersListFilter.districtFilter);
	const schoolFilter = useAppSelector((state) => state.usersListFilter.schoolFilter);
	const roleFilter = useAppSelector((state) => state.usersListFilter.roleFilter);
	const includeInactive = useAppSelector((state) => state.usersListFilter.includeInactive);
	const sortField = useAppSelector((state) => state.usersListFilter.sortField);
	const sortOrder = useAppSelector((state) => state.usersListFilter.sortOrder);
	const page = useAppSelector((state) => state.usersListFilter.page);

	const previousRole = useRef<UserRole | null>(role);

	useEffect(() => {
		if (previousRole.current !== role) {
			dispatch(resetUsersListFilters());
		}
		previousRole.current = role;
	}, [role, dispatch]);

	return {
		searchFilter,
		districtFilter,
		schoolFilter,
		roleFilter,
		includeInactive,
		sortField,
		sortOrder,
		page,
		setSearchFilter: (value: string) => dispatch(setSearchFilter(value)),
		setDistrictFilter: (value: number) => dispatch(setDistrictFilter(value)),
		setSchoolFilter: (value: number) => dispatch(setSchoolFilter(value)),
		setRoleFilter: (value: string) => dispatch(setRoleFilter(value)),
		setIncludeInactive: (value: boolean) => dispatch(setIncludeInactive(value)),
		setSortField: (value: string) => dispatch(setSortField(value)),
		setSortOrder: (value: SortOrder) => dispatch(setSortOrder(value)),
		setPage: (value: number) => dispatch(setPage(value)),
	};
}
